/**
 * BreakdownRow — displays a single pot contribution within an aggregated payment.
 * Shows a pot-type avatar (gang vs solo), pot reference, status, and amount.
 */

import { Avatar, GroupsIcon, PersonIcon, Text } from '@billda/design-system';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PaymentRowDto } from '../api/paymentsApi';

export const GANG_PREFIX = 'Gang - ';

interface BreakdownRowProps {
  row: PaymentRowDto;
  'data-testid'?: string;
}

export function BreakdownRow({
  row,
  'data-testid': dataTestId,
}: BreakdownRowProps) {
  const isGang = row.potReference.startsWith(GANG_PREFIX);
  const isPaid = row.potStatus === 'Paid';
  const OwnerIcon = isGang ? GroupsIcon : PersonIcon;

  return (
    <div
      data-testid={dataTestId}
      className="flex items-center gap-3 border-b border-border-default px-3 py-3 last:border-b-0"
    >
      <Avatar
        size="sm"
        className="shrink-0 bg-surface-raised text-content-secondary"
      >
        <OwnerIcon aria-hidden="true" className="size-[18px]" />
      </Avatar>

      <div className="min-w-0 flex-1">
        <Text variant="body2" className="truncate font-semibold">
          {row.potReference}
        </Text>
        <Text
          variant="caption"
          className={isPaid ? 'text-content-success' : 'text-content-secondary'}
        >
          {row.potStatus}
        </Text>
      </div>

      <Text variant="body2" className="shrink-0 font-semibold">
        {formatCurrency(row.amountDue)}
      </Text>
    </div>
  );
}
